import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { getCategoryMetadata, useSubcategories } from '~/entities/categories'
import { getReviewStatus, PRIOR_AUTH_PHASES } from '~/entities/document'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { TABLE_COLUMNS } from '../config'
import { useFilters, usePaginationSelectors } from '../model'

export function useDocuments() {
  const { filters } = useFilters()
  const { currentPage, evaluationKeysMap } = usePaginationSelectors({ pageSize: filters.pageSize })
  const queryClient = useQueryClient()
  const subcategories = useSubcategories()
  const isHideLlmClassificationEnabled = useIsFeatureEnabled('2026-08-hide-llm-classification')

  // Cancel query on unmount
  useEffect(() => {
    return () => {
      queryClient.cancelQueries({ queryKey: apiClient.listDocumentsDocumentsGetQueryKey() })
    }
  }, [queryClient])

  const classificationPhase = filters.classification
    ? getCategoryMetadata({ category: filters.classification, subcategories })?.phase.label
    : undefined
  const isPaClassificationFilter =
    filters.phase.some((p) => PRIOR_AUTH_PHASES.includes(p)) ||
    (classificationPhase != null && PRIOR_AUTH_PHASES.includes(classificationPhase))
  const effectiveStatus: typeof filters.status =
    isHideLlmClassificationEnabled &&
    isPaClassificationFilter &&
    filters.classification &&
    filters.status.includes('awaiting')
      ? ['finished']
      : filters.status

  return useSuspenseQuery({
    ...apiClient.listDocumentsDocumentsGetOptions({
      query: {
        limit: filters.pageSize,
        review_status: getReviewStatus(effectiveStatus),
        category_phase: filters.phase[0],
        document_key: filters.assetId.startsWith('doc-') ? filters.assetId : null,
        consignment_key: filters.assetId.startsWith('cns-') ? filters.assetId : null,
        reviewer_sub: filters.reviewer || null,
        last_evaluated_key: evaluationKeysMap[currentPage],
        inbox_name: filters.source || null,
        date_from: filters.dateFrom || null,
        date_to: filters.dateTo || null,
        scan_index_forward:
          filters.sortBy === TABLE_COLUMNS.DATE_RECEIVED && filters.sortOrder === 'asc'
            ? true
            : undefined,
        payor_id: filters.payor || null,
        visibility: filters.visibility === 'onshore' ? 'onshore_only' : null,
        category: filters.classification || null,
        flag_status: filters.flagged === 'flagged' ? 'pending' : null,
      },
    }),
    refetchInterval: 1000 * 30,
  })
}
